import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
  ZAxis,
} from 'recharts'
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded'
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded'
import DropDownFilter from '../components/DropDownFilter'
import Header from '../components/Header'
import MenuBar from '../components/MenuBar'
import httpClient from '../components/helpers/httpClient'
import './DashboardPage.css'

const INVENTORY_SERIES = [
  { key: 'available', label: 'Available', color: '#cfcfcf' },
  { key: 'sold', label: 'Sold', color: '#4caf50' },
  { key: 'rented', label: 'Rented', color: '#2196f3' },
  { key: 'inactive', label: 'Inactive', color: '#5f5f5f' },
]

const ENQUIRY_SERIES = [
  { key: 'new', label: '0–2 days', color: '#14806f' },
  { key: 'waiting', label: '3–7 days', color: '#e2ad48' },
  { key: 'overdue', label: '8+ days', color: '#c66152' },
]
const TIMELINE_COLORS = ['#124b55', '#278d93', '#6f94b8', '#b7863a', '#756f9c', '#658b72']
const PERIOD_OPTIONS = [
  { id: 6, name: 'Last 6 months' },
  { id: 12, name: 'Last 12 months' },
  { id: 24, name: 'Last 24 months' },
]

const moneyFormatter = new Intl.NumberFormat('en-BE', {
  style: 'currency',
  currency: 'EUR',
  notation: 'compact',
  maximumFractionDigits: 1,
})
const numberFormatter = new Intl.NumberFormat('en-BE')

const formatMoney = value => moneyFormatter.format(Number(value) || 0)
const formatNumber = value => numberFormatter.format(Number(value) || 0)
const formatPercent = value => `${Math.round(Number(value) || 0)}%`
const formatMonth = (month, includeYear = false) => {
  const date = new Date(`${month}-01T12:00:00`)
  if (Number.isNaN(date.valueOf())) return month
  return new Intl.DateTimeFormat('en-BE', {
    month: 'short',
    year: includeYear ? '2-digit' : undefined,
  }).format(date)
}
const formatDate = value => {
  const date = new Date(value)
  if (Number.isNaN(date.valueOf())) return value
  return new Intl.DateTimeFormat('en-BE', { day: 'numeric', month: 'short', year: 'numeric' }).format(date)
}
const formatTimelineTick = value => new Intl.DateTimeFormat('en-BE', {
  month: 'short',
  year: '2-digit',
}).format(new Date(value))

function Legend({ items }) {
  return (
    <div className="dashboard-legend" aria-label="Chart legend">
      {items.map(item => (
        <span key={item.key} className="dashboard-legend__item">
          <i style={{ '--legend-color': item.color }} />
          {item.label}
        </span>
      ))}
    </div>
  )
}

function TooltipShell({ title, children }) {
  return (
    <div className="dashboard-tooltip">
      <strong>{title}</strong>
      <div className="dashboard-tooltip__rows">{children}</div>
    </div>
  )
}

function TooltipRow({ color, label, value }) {
  return (
    <span>
      <i style={{ '--tooltip-color': color }} />
      <em>{label}</em>
      <b>{value}</b>
    </span>
  )
}

function InventoryTooltip({ active, payload }) {
  if (!active || !payload?.length) return null
  const item = payload[0].payload
  return (
    <TooltipShell title={item.name}>
      {INVENTORY_SERIES.map(series => (
        <TooltipRow
          key={series.key}
          color={series.color}
          label={series.label}
          value={formatNumber(item[`${series.key}_count`])}
        />
      ))}
      <TooltipRow color="#1e1e1e" label="Total" value={formatNumber(item.total)} />
    </TooltipShell>
  )
}

function SalesTooltip({ active, payload, mode }) {
  if (!active || !payload?.length) return null
  const item = payload[0].payload
  return (
    <TooltipShell title={formatMonth(item.month, true)}>
      {mode === 'value' ? (
        <TooltipRow color="#124b55" label="Contracted value" value={formatMoney(item.value)} />
      ) : (
        <>
          <TooltipRow color="#e2ad48" label="Reservation dates" value={formatNumber(item.reservations)} />
          <TooltipRow color="#278d93" label="Contract dates" value={formatNumber(item.contracts)} />
          <TooltipRow color="#124b55" label="Deed dates" value={formatNumber(item.deeds)} />
        </>
      )}
    </TooltipShell>
  )
}

function EnquiryTooltip({ active, payload }) {
  if (!active || !payload?.length) return null
  const item = payload[0].payload
  return (
    <TooltipShell title={item.source}>
      {ENQUIRY_SERIES.map(series => (
        <TooltipRow
          key={series.key}
          color={series.color}
          label={series.label}
          value={formatNumber(item[series.key])}
        />
      ))}
    </TooltipShell>
  )
}

function PricingTooltip({ active, payload }) {
  if (!active || !payload?.length) return null
  const item = payload[0].payload
  return (
    <TooltipShell title={`${item.project} · ${item.unit}`}>
      <TooltipRow color={item.color} label="Status" value={item.status} />
      <TooltipRow color={item.color} label="Habitable area" value={`${formatNumber(item.area)} m²`} />
      <TooltipRow color={item.color} label="Commercial price" value={formatMoney(item.price)} />
      <TooltipRow color={item.color} label="Price / m²" value={`${formatMoney(item.price_per_m2)}/m²`} />
    </TooltipShell>
  )
}

function TimelineTooltip({ active, payload }) {
  if (!active || !payload?.length) return null
  const item = payload[0].payload
  return (
    <TooltipShell title={item.building}>
      <TooltipRow color={item.color} label="Project" value={item.project} />
      <TooltipRow color={item.color} label="Start" value={formatDate(item.start)} />
      <TooltipRow color={item.color} label="End" value={formatDate(item.end)} />
    </TooltipShell>
  )
}

function PanelHeader({ title, description, action, children }) {
  return (
    <div className="dashboard-panel__header">
      <div>
        <h2>{title}</h2>
        <p>{description}</p>
      </div>
      {children || (action && (
        <Link to={action.to} className="dashboard-panel__link">
          {action.label}
          <ArrowForwardRoundedIcon fontSize="small" />
        </Link>
      ))}
    </div>
  )
}

function EmptyChart({ children }) {
  return (
    <div className="dashboard-empty">
      <span />
      <p>{children}</p>
    </div>
  )
}

function DashboardLoading() {
  return (
    <div className="dashboard-loading" aria-label="Loading dashboard">
      <div className="dashboard-loading__metrics" />
      <div className="dashboard-loading__grid">
        <div />
        <div />
        <div />
        <div />
      </div>
    </div>
  )
}

function Dashboard() {
  const [dashboard, setDashboard] = useState(null)
  const [projectId, setProjectId] = useState('')
  const [months, setMonths] = useState(12)
  const [salesMode, setSalesMode] = useState('units')
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState('')
  const todayTimestamp = useMemo(() => Date.now(), [])

  useEffect(() => {
    document.querySelector('#header-title')?.classList.remove('hide')
  }, [])

  const fetchDashboard = useCallback(async ({ quiet = false } = {}) => {
    if (quiet) setRefreshing(true)
    else setLoading(true)
    setError('')

    try {
      const response = await httpClient.get(`${import.meta.env.VITE_API_URL}/api/dashboard/summary`, {
        params: {
          months,
          ...(projectId ? { project_id: projectId } : {}),
        },
      })
      if (response.status !== 200) throw new Error(response.data?.error || 'Dashboard request failed')
      setDashboard(response.data)
    } catch (requestError) {
      setError(requestError.response?.data?.error || requestError.message || 'Dashboard data could not be loaded')
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [months, projectId])

  useEffect(() => {
    fetchDashboard()
  }, [fetchDashboard])

  const inventoryData = useMemo(() => (dashboard?.inventory || [])
    .filter(item => item.total > 0)
    .map(item => {
      const transformed = { ...item }
      INVENTORY_SERIES.forEach(series => {
        transformed[`${series.key}_count`] = item[series.key]
        transformed[series.key] = (item[series.key] / item.total) * 100
      })
      return transformed
    }), [dashboard])

  const pricingByStatus = useMemo(() => INVENTORY_SERIES.map(series => ({
    ...series,
    data: (dashboard?.pricing || [])
      .filter(item => item.status === series.key)
      .map(item => ({ ...item, color: series.color })),
  })), [dashboard])

  const timelineData = useMemo(() => {
    const projectColors = new Map()
    return (dashboard?.construction || []).map(item => {
      if (!projectColors.has(item.project)) {
        projectColors.set(item.project, TIMELINE_COLORS[projectColors.size % TIMELINE_COLORS.length])
      }
      return {
        ...item,
        range: [new Date(item.start).getTime(), new Date(item.end).getTime()],
        color: projectColors.get(item.project),
      }
    })
  }, [dashboard])

  const timelineDomain = useMemo(() => {
    if (!timelineData.length) return [0, 1]
    const values = timelineData.flatMap(item => item.range)
    values.push(todayTimestamp)
    const minimum = Math.min(...values)
    const maximum = Math.max(...values)
    const span = Math.max(maximum - minimum, 86400000)
    return [minimum - span * 0.03, maximum + span * 0.06]
  }, [timelineData, todayTimestamp])

  const kpis = dashboard?.kpis || {}
  const lastUpdated = dashboard?.generated_at ? formatDate(dashboard.generated_at) : ''
  const monthTicksIncludeYear = months > 12
  const projectOptions = [
    { id: '', name: 'All portfolio projects' },
    ...(dashboard?.projects || []),
  ]
  const projectFilterValue = projectOptions.find(option => String(option.id) === String(projectId))?.name || ''
  const periodFilterValue = PERIOD_OPTIONS.find(option => option.id === months)?.name || ''

  return (
    <div className="template">
      <Header>
        <div className="header__filters dashboard-header-filters">
          <DropDownFilter
            idList="dashboard-filter-project"
            placeholderText="All portfolio projects"
            listArray={projectOptions}
            value={projectFilterValue}
            clearable={false}
            onChange={option => setProjectId(option.id === '' ? '' : String(option.id))}
          />
          <DropDownFilter
            idList="dashboard-filter-period"
            placeholderText="Last 12 months"
            listArray={PERIOD_OPTIONS}
            value={periodFilterValue}
            clearable={false}
            onChange={option => setMonths(option.id)}
          />
          <button
            type="button"
            className="dashboard-refresh"
            onClick={() => fetchDashboard({ quiet: true })}
            disabled={refreshing}
            aria-label="Refresh dashboard"
          >
            <RefreshRoundedIcon className={refreshing ? 'is-spinning' : ''} />
          </button>
        </div>
      </Header>
      <MenuBar />

      <div className="template__body" id="body-dashboard">
        <main className="dashboard-content">
          {error && dashboard && (
            <div className="dashboard-notice" role="alert">
              <span>Refresh failed. Showing previous data.</span>
              <button type="button" onClick={() => fetchDashboard({ quiet: true })}>Try again</button>
            </div>
          )}

          {loading && !dashboard ? (
            <DashboardLoading />
          ) : error && !dashboard ? (
            <div className="dashboard-error" role="alert">
              <h2>Dashboard data unavailable</h2>
              <p>{error}</p>
              <button type="button" onClick={() => fetchDashboard()}>Try again</button>
            </div>
          ) : (
            <>
              <section className="dashboard-kpis" aria-label="Portfolio summary">
                <div>
                  <strong>{formatNumber(kpis.projects)}</strong>
                  <span>Portfolio projects</span>
                </div>
                <div>
                  <strong>{formatNumber(kpis.available_units)}</strong>
                  <span>Available homes</span>
                </div>
                <div>
                  <strong>{formatMoney(kpis.available_value)}</strong>
                  <span>Available asking value</span>
                </div>
                <div>
                  <strong>{formatMoney(kpis.sales_value)}</strong>
                  <span>Contracted value</span>
                </div>
                <div>
                  <strong>{formatNumber(kpis.pending_enquiries)}</strong>
                  <span>Open enquiries</span>
                </div>
              </section>

              <section className="dashboard-grid">
                <article className="dashboard-panel dashboard-panel--inventory">
                  <PanelHeader
                    title="Inventory by project"
                    description="Share of units at each commercial stage."
                    action={{ to: '/projects', label: 'View projects' }}
                  />
                  <Legend items={INVENTORY_SERIES} />
                  {inventoryData.some(item => item.total) ? (
                    <div className="dashboard-chart dashboard-chart--inventory">
                      <ResponsiveContainer width="100%" height={Math.max(136, inventoryData.length * 32 + 36)}>
                        <BarChart accessibilityLayer layout="vertical" data={inventoryData} margin={{ top: 8, right: 20, bottom: 4, left: 8 }}>
                          <CartesianGrid horizontal={false} stroke="#e5e9e7" />
                          <XAxis type="number" domain={[0, 100]} tickFormatter={formatPercent} axisLine={false} tickLine={false} />
                          <YAxis type="category" dataKey="name" width={112} axisLine={false} tickLine={false} tick={{ fill: '#42595d', fontSize: 12 }} />
                          <Tooltip cursor={{ fill: 'rgba(18, 75, 85, 0.045)' }} content={<InventoryTooltip />} />
                          {INVENTORY_SERIES.map(series => (
                            <Bar key={series.key} dataKey={series.key} stackId="inventory" fill={series.color} maxBarSize={18} />
                          ))}
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  ) : <EmptyChart>No units found for this project selection.</EmptyChart>}
                </article>

                <article className="dashboard-panel dashboard-panel--sales">
                  <PanelHeader title="Sales momentum" description="Recorded transaction milestones over time.">
                    <div className="dashboard-segmented" aria-label="Sales chart metric">
                      <button type="button" aria-pressed={salesMode === 'units'} className={salesMode === 'units' ? 'is-active' : ''} onClick={() => setSalesMode('units')}>Units</button>
                      <button type="button" aria-pressed={salesMode === 'value'} className={salesMode === 'value' ? 'is-active' : ''} onClick={() => setSalesMode('value')}>Value</button>
                    </div>
                  </PanelHeader>
                  <Legend items={salesMode === 'value' ? [{ key: 'value', label: 'Contracted value', color: '#124b55' }] : [
                    { key: 'reservations', label: 'Reservation dates', color: '#e2ad48' },
                    { key: 'contracts', label: 'Contract dates', color: '#278d93' },
                    { key: 'deeds', label: 'Deed dates', color: '#124b55' },
                  ]} />
                  {(dashboard?.sales_trend || []).length ? (
                    <div className="dashboard-chart dashboard-chart--sales">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart accessibilityLayer data={dashboard.sales_trend} margin={{ top: 16, right: 8, bottom: 4, left: 0 }} barGap={3}>
                          <CartesianGrid vertical={false} stroke="#e5e9e7" />
                          <XAxis dataKey="month" tickFormatter={value => formatMonth(value, monthTicksIncludeYear)} axisLine={false} tickLine={false} minTickGap={14} />
                          <YAxis tickFormatter={salesMode === 'value' ? formatMoney : formatNumber} axisLine={false} tickLine={false} width={salesMode === 'value' ? 58 : 32} allowDecimals={false} />
                          <Tooltip cursor={{ fill: 'rgba(18, 75, 85, 0.045)' }} content={<SalesTooltip mode={salesMode} />} />
                          {salesMode === 'value' ? (
                            <Bar dataKey="value" fill="#124b55" radius={[5, 5, 0, 0]} maxBarSize={28} />
                          ) : (
                            <>
                              <Bar dataKey="reservations" fill="#e2ad48" radius={[4, 4, 0, 0]} maxBarSize={16} />
                              <Bar dataKey="contracts" fill="#278d93" radius={[4, 4, 0, 0]} maxBarSize={16} />
                              <Bar dataKey="deeds" fill="#124b55" radius={[4, 4, 0, 0]} maxBarSize={16} />
                            </>
                          )}
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  ) : <EmptyChart>No sales activity in selected period.</EmptyChart>}
                </article>

                <article className="dashboard-panel dashboard-panel--enquiries">
                  <PanelHeader
                    title="Enquiry queue"
                    description="Pending website enquiries grouped by age."
                    action={{ to: '/contacts/new', label: 'Open queue' }}
                  />
                  <Legend items={ENQUIRY_SERIES} />
                  {(dashboard?.enquiries || []).length ? (
                    <div className="dashboard-chart dashboard-chart--enquiries">
                      <ResponsiveContainer width="100%" height={Math.max(92, dashboard.enquiries.length * 30 + 24)}>
                        <BarChart accessibilityLayer layout="vertical" data={dashboard.enquiries} margin={{ top: 10, right: 16, bottom: 4, left: 4 }}>
                          <XAxis type="number" hide allowDecimals={false} />
                          <YAxis type="category" dataKey="source" width={96} axisLine={false} tickLine={false} tick={{ fill: '#42595d', fontSize: 11 }} />
                          <Tooltip cursor={{ fill: 'rgba(18, 75, 85, 0.045)' }} content={<EnquiryTooltip />} />
                          {ENQUIRY_SERIES.map(series => (
                            <Bar key={series.key} dataKey={series.key} stackId="age" fill={series.color} maxBarSize={17} />
                          ))}
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  ) : <EmptyChart>Queue clear. No pending enquiries.</EmptyChart>}
                </article>

                <article className="dashboard-panel dashboard-panel--pricing">
                  <PanelHeader title="Inventory price positioning" description="Commercial price per m² across unit sizes." />
                  <Legend items={INVENTORY_SERIES} />
                  {(dashboard?.pricing || []).length ? (
                    <div className="dashboard-chart dashboard-chart--pricing">
                      <ResponsiveContainer width="100%" height="100%">
                        <ScatterChart accessibilityLayer margin={{ top: 12, right: 16, bottom: 12, left: 4 }}>
                          <CartesianGrid stroke="#e5e9e7" />
                          <XAxis type="number" dataKey="area" name="Area" unit=" m²" axisLine={false} tickLine={false} tickFormatter={formatNumber} />
                          <YAxis type="number" dataKey="price_per_m2" name="Price per m²" axisLine={false} tickLine={false} tickFormatter={formatMoney} width={58} />
                          <ZAxis range={[72, 72]} />
                          <Tooltip cursor={{ stroke: '#9caeaa', strokeDasharray: '4 4' }} content={<PricingTooltip />} />
                          {pricingByStatus.map(series => (
                            <Scatter
                              key={series.key}
                              name={series.label}
                              data={series.data}
                              fill={series.color}
                              stroke={series.key === 'available' ? '#5f5f5f' : series.color}
                              strokeWidth={series.key === 'available' ? 1 : 0}
                            />
                          ))}
                        </ScatterChart>
                      </ResponsiveContainer>
                    </div>
                  ) : <EmptyChart>Add commercial prices and habitable areas to compare units.</EmptyChart>}
                </article>

                <article className="dashboard-panel dashboard-panel--timeline">
                  <PanelHeader title="Construction programme" description="Planned building windows with today marker." />
                  {timelineData.length ? (
                    <div className="dashboard-chart dashboard-chart--timeline">
                      <ResponsiveContainer width="100%" height={Math.max(112, timelineData.length * 30 + 50)}>
                        <BarChart accessibilityLayer layout="vertical" data={timelineData} margin={{ top: 12, right: 12, bottom: 4, left: 8 }}>
                          <CartesianGrid horizontal={false} stroke="#e5e9e7" />
                          <XAxis type="number" dataKey="range" scale="time" domain={timelineDomain} tickFormatter={formatTimelineTick} axisLine={false} tickLine={false} minTickGap={22} />
                          <YAxis type="category" dataKey="building" width={105} axisLine={false} tickLine={false} tick={{ fill: '#42595d', fontSize: 11 }} />
                          <Tooltip cursor={{ fill: 'rgba(18, 75, 85, 0.045)' }} content={<TimelineTooltip />} />
                          <ReferenceLine x={todayTimestamp} stroke="#c66152" strokeWidth={1.5} strokeDasharray="4 4" label={{ value: 'Today', position: 'insideTopRight', fill: '#9a463b', fontSize: 10 }} />
                          <Bar dataKey="range" radius={5} maxBarSize={17}>
                            {timelineData.map(item => <Cell key={item.building_id} fill={item.color} />)}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  ) : <EmptyChart>Add construction start and end dates to see programme.</EmptyChart>}
                </article>
              </section>

              <footer className="dashboard-footer">
                <span>Updated {lastUpdated}</span>
                <span>Commercial values exclude taxes.</span>
              </footer>
            </>
          )}
        </main>
      </div>
    </div>
  )
}

export default Dashboard
